import React, { useEffect, useRef, useState } from "react";
import { IDog } from "@eagerdog/interfaces";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import { Modal, useModal } from "src/components/Modal/Modal";
import DogForm from "src/components/StepForm/DogForm/DogForm";

import DogItem from "../DogItem/DogItem";

import styles from "./MyDogs.module.scss";

function sortByNewest(dogs: IDog[]): IDog[] {
    return [...dogs].sort((a, b) => {
        const tsA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tsB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tsB - tsA;
    });
}

const MyDogs: React.FC = () => {
    const user = helperService.getUser();
    const scrollerRef = useRef<HTMLUListElement>(null);

    const [dogs, setDogs] = useState<IDog[]>([]);
    const [canScroll, setCanScroll] = useState<boolean>(false);
    const editModal = useModal();

    useEffect(() => {
        if (!user._id) return;
        apiService.getUserDogs(user._id).then((response: IDog[]) => {
            setDogs(sortByNewest(response || []));
        }).catch(() => {});
    }, [user._id]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;

        const update = () => {
            setCanScroll(el.scrollWidth - el.clientWidth > 4 && el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
        };

        update();
        el.addEventListener("scroll", update);
        window.addEventListener("resize", update);
        return () => {
            el.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, [dogs]);

    const scrollNext = () => {
        scrollerRef.current?.scrollBy({ left: 160, behavior: "smooth" });
    };

    return (
        <div className={styles.MyDogs}>
            <div className={styles.header}>
                <div className={styles.heading}>
                    <span className={styles.title}>My dogs</span>
                    <span className={styles.subtitle}>Choose a dog to see what's next</span>
                </div>
                <button
                    type="button"
                    className={styles.addBtn}
                    onClick={() => editModal.toggle()}
                >
                    <span className={styles.plus}>+</span>
                    Add dog
                </button>
            </div>
            {dogs.length > 0 && (
                <div className={styles.row}>
                    <ul className={styles.list} ref={scrollerRef}>
                        {dogs.map((dog) => (
                            <DogItem key={dog._id} dog={dog} />
                        ))}
                    </ul>
                    {canScroll && (
                        <button type="button" className={styles.scrollBtn} onClick={scrollNext} aria-label="More dogs">
                            <span />
                        </button>
                    )}
                </div>
            )}

            <Modal
                className="dogForm"
                isShown={editModal.isShown}
                hide={editModal.toggle}
                modalContent={
                    <DogForm
                        onFinish={(updatedDog) => {
                            if (updatedDog) {
                                setDogs((prev) => sortByNewest([...prev, updatedDog]));
                            }
                            editModal.toggle();
                        }}
                    />
                }
            />
        </div>
    );
};

export default MyDogs;
